import React from 'react';
import { useNavigate } from 'react-router-dom';
import '../../App.css';
import '../data-management/data-management.css';
import '../../tools/search-results/search-results.css';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '../../app/hooks';

// types
import {
  CavernFileType,
  DataItemType,
  Project,
  RucioLocationsType,
  Site,
  SiteStorageArea,
  toolTypeToPath,
  ToolType,
} from '../../utils/types';
import { ToolButtonType } from '../../tools/tool-button';

// functions
import { fn_updateIcon } from '../../utils/functions';

// classes
import RucioFilesTable from './rucio-files-table';
import CavernFilesTable from './cavern-files-table';
import ComputeServicesPanel from '../panels/compute-services-panel';
import MiniButton from '../../tools/mini-button';
import Scrollbox from '../../tools/scrollbox';
import ToolButton from '../../tools/tool-button';
import { TabBar } from '../../tools/tab-component/tab-component';
import ProjectIcon from '../../tools/project-icon';

// icons
import { CircleAlert, ArrowLeft, RefreshCw } from 'lucide-react';

//  --------------------------------------------------------------------------
//
//  R E D U X   S T A T E   M A N A G E M E N T
//
//  --------------------------------------------------------------------------

// import state definition and functions (redux).
import { getRucioSites } from '../data-management/file-browser-actions';
import { getProjectFiles, getUserProjectFiles } from './project-actions';
import { setSelectedTool } from '../../app-slice';

//  --------------------------------------------------------------------------
//
//  P R O P E R T I E S
//
//  --------------------------------------------------------------------------

//  --------------------------------------------------------------------------
//
//  C L A S S   D E F I N I T I O N
//
//  --------------------------------------------------------------------------

export default function ProjectFiles() {
  //
  //  --------------------------------------------------------------------------
  //
  //  R E D U X   S T A T E   H A N D L I N G
  //
  //  --------------------------------------------------------------------------

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // global state parameters.
  const sG_project: Project | undefined = useAppSelector((state) => state.global.project);
  const sG_loggedIn: boolean | undefined = useAppSelector(
    (state) => state.global.loggedIn,
  );
  const sG_darkMode: boolean = useAppSelector((state) => state.global.darkMode);

  // data state parameters.
  const sD_projects: Project[] = useAppSelector((state) => state.data.projects);

  // the data items displayed in the table.
  const sProjectItems: DataItemType[] = useAppSelector(
    (state) => state.project.projectItems,
  );
  const sCavernItems: CavernFileType[] = useAppSelector(
    (state) => state.project.userItems,
  );

  //  --------------------------------------------------------------------------
  //
  //  D E C L A R A T I O N S
  //
  //  --------------------------------------------------------------------------

  // translation function
  const { t } = useTranslation();

  // retrieve the list of sites (in availability order) for the project
  // items and selected project items.
  const _sites: {
    site: Site;
    count: number;
  }[] = getRucioSites({
    allItems: sProjectItems,
    catalog: 'project-files',
  });

  //  --------------------------------------------------------------------------
  //
  //  E V E N T   D E C L A R A T I O N S
  //
  //  --------------------------------------------------------------------------

  //  --------------------------------------------------------------------------
  //
  //  F U N C T I O N S
  //
  //  --------------------------------------------------------------------------

  //  ------------------------------------------------------------
  //
  //  Handler button clicks.
  //
  //  ------------------------------------------------------------

  const onClickBtnHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    //
    //
  }; // onClickBtnHandler

  //  --------------------------------------------------------------------------
  //
  //  C O M P O N E N T S
  //
  //  --------------------------------------------------------------------------

  //  --------------------------------------------------------------------------
  //
  //  H O O K S
  //
  //  --------------------------------------------------------------------------

  //  ------------------------------------------------------------
  //
  //  C O M P O N E N T   C O D E
  //
  //  ------------------------------------------------------------

  return (
    //
    <div
      style={{
        flex: '1 1',
        height: '100%',
        marginRight: '10px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        className="panel-main text-colour"
        role="region"
        aria-label="Project"
        style={{
          position: 'relative',
          height: '100%',
          flex: '1 1',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* header */}
        <div
          style={{
            flex: '0 0 auto',
            display: 'flex',
            flexDirection: 'row',
            margin: '3px 3px 15px 3px',
            alignItems: 'center',
          }}
        >
          <button
            type="button"
            className="text-colour"
            style={{
              flex: '0 0 auto',
              border: 'none',
              backgroundColor: 'transparent',
              marginRight: '10px',
            }}
            title="Back to home"
            onClick={() => {
              dispatch(setSelectedTool(ToolType.HOME));
              navigate(toolTypeToPath[ToolType.HOME]);
            }}
          >
            <ArrowLeft width={40} height={40} strokeWidth={1.5} />
          </button>
          <ProjectIcon project={sG_project} size={50} />
          <div
            style={{
              flex: '1 1',
              display: 'flex',
              flexDirection: 'column',
              marginLeft: '15px',
            }}
          >
            <div
              role="heading"
              aria-level={2}
              className="header-text"
              style={{
                flex: '0 0 auto',
                marginBottom: '5px',
              }}
            >
              {sG_loggedIn === true && sG_project !== undefined
                ? 'Project: ' + (sG_project?.name ?? 'unknown').toUpperCase()
                : 'No project selected'}
            </div>
            <div
              style={{
                flex: '0 0 auto',
              }}
            >
              {sProjectItems.length + sCavernItems.length} file(s) in project
            </div>
          </div>
          <ToolButton
            key={t('Download all')}
            name="download-all"
            onClick={onClickBtnHandler}
            tooltip="Download all project files"
            text={t('Download all')}
            disabled={sProjectItems.length + sCavernItems.length === 0}
            type={ToolButtonType.SECONDARY}
          />
          <div style={{ flex: '0 0 15px' }} />
          <ToolButton
            key={t('Upload file(s)')}
            name="upload-files"
            onClick={onClickBtnHandler}
            tooltip="Upload files to project"
            text={t('Upload file(s)')}
            type={ToolButtonType.SECONDARY}
          />
          <div style={{ flex: '0 0 25px' }} />
        </div>

        <Scrollbox direction="vertical" width="100%" flex="1 1" flexDirection="column">
          <TabBar
            style={{
              margin: '10px 0px 15px 0px',
              padding: '0px 0px 0px 15px',
            }}
          >
            <div
              className="text-colour"
              style={{
                flex: '1 1',
                margin: '0px 0px 0px 3px',
              }}
            >
              Observatory data product(s) / advanced data product(s):
            </div>
            <div
              className="text-colour"
              style={{
                flex: '0 0 auto',
                margin: '0px 18px 0px 0px',
              }}
            >
              <MiniButton
                className="text-colour"
                data-mini-button="T"
                onClick={() => dispatch(getProjectFiles())}
              >
                <RefreshCw width={30} height={30} strokeWidth={1.5} />
              </MiniButton>
            </div>
          </TabBar>

          <RucioFilesTable />

          <TabBar
            style={{
              margin: '25px 0px 15px 0px',
              padding: '0px 0px 0px 15px',
            }}
          >
            <div
              className="text-colour"
              style={{
                flex: '1 1',
                margin: '0px 0px 0px 3px',
              }}
            >
              User-generated data product(s):
            </div>
            <div
              className="text-colour"
              style={{
                flex: '0 0 auto',
                margin: '0px 18px 0px 0px',
              }}
            >
              <MiniButton
                className="text-colour"
                data-mini-button="T"
                onClick={() => dispatch(getUserProjectFiles())}
              >
                <RefreshCw width={30} height={30} strokeWidth={1.5} />
              </MiniButton>
            </div>
          </TabBar>

          <CavernFilesTable />
        </Scrollbox>
      </div>

      <ComputeServicesPanel
        sites={_sites.length > 0 ? [_sites[0].site] : []}
        flex="0 0 auto"
        stagingCatalog="project-files"
      />
    </div>
  );
} // ProjectFiles
